import { useCallback, useEffect, useMemo, useState } from 'react';

import { stringToFile } from '../../../utils/filesystem';
import { postDocumentacionOtros } from '../Documentacion/useDocumentacion';
import { useComprobante } from './useComprobante';

export type ComprobanteStatus = 'pending' | 'submitting' | 'success' | 'error';

export type TempComprobanteFile = {
  id: string;
  file: File;
  preview: string;
  createdAt: number;
};

export type ComprobanteFile = TempComprobanteFile & {
  status: ComprobanteStatus;
  errorMessage?: string;
};

export type ComprobanteItem = {
  id: string;
  concepto: string;
  monto: string;
  files: ComprobanteFile[];
  status: ComprobanteStatus;
  errorMessage?: string;
  createdAt: number;
};

type GalleryState = {
  isOpen: boolean;
  fileUrl: string | null;
  mimeType: string | null;
  comprobanteId: string | null;
  comprobanteFileId: string | null;
  tempFileId: string | null;
};

const createObjectUrl = (file: File): string => URL.createObjectURL(file);

const revokeObjectUrl = (url?: string | null): void => {
  if (url) URL.revokeObjectURL(url);
};

const MONTO_ARCHIVO_ADICIONAL = '0';

const getComprobanteStatus = (files: ComprobanteFile[]): ComprobanteStatus => {
  if (files.some((file) => file.status === 'submitting')) return 'submitting';
  if (files.some((file) => file.status === 'error')) return 'error';
  if (files.length > 0 && files.every((file) => file.status === 'success')) {
    return 'success';
  }
  return 'pending';
};

/**
 * Hook consolidado que maneja toda la lógica de comprobantes:
 * - Formulario y archivos temporales
 * - Lista de comprobantes
 * - Galería de visualización
 * - Envío y estados
 */
export const useComprobanteManager = (isVacuna?: boolean) => {
  const { postComprobante } = useComprobante();

  // Estado de comprobantes
  const [groups, setGroups] = useState<ComprobanteItem[]>([]);

  // Estado del formulario
  const [showForm, setShowForm] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [tempFiles, setTempFiles] = useState<TempComprobanteFile[]>([]);

  // Estado de la galería
  const [galleryState, setGalleryState] = useState<GalleryState>({
    isOpen: false,
    fileUrl: null,
    mimeType: null,
    comprobanteId: null,
    comprobanteFileId: null,
    tempFileId: null,
  });

  // Mostrar formulario automáticamente cuando no hay comprobantes
  useEffect(() => {
    if (groups.length === 0) {
      setShowForm(true);
    }
  }, [groups.length]);

  // === Manejo de archivos temporales ===
  const handleFileSelected = useCallback(async (file: File | string) => {
    let finalFile: File;
    if (file instanceof File) {
      finalFile = file;
    } else if (typeof file === 'string') {
      finalFile = await stringToFile(file, 'comprobante');
    } else {
      return;
    }

    const preview = createObjectUrl(finalFile);
    setTempFiles((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        file: finalFile,
        preview,
        createdAt: Date.now(),
      },
    ]);
  }, []);

  const clearTempFiles = useCallback(() => {
    setTempFiles((prev) => {
      prev.forEach((item) => revokeObjectUrl(item.preview));
      return [];
    });
  }, []);

  const removeTempFile = useCallback((id: string) => {
    setTempFiles((prev) => {
      const item = prev.find((temp) => temp.id === id);
      revokeObjectUrl(item?.preview);
      return prev.filter((temp) => temp.id !== id);
    });
  }, []);

  const clearTempFile = clearTempFiles;

  // === Manejo de comprobantes ===
  const addComprobante = useCallback(
    (concepto: string, monto: string) => {
      if (!tempFiles.length) return;

      setGroups((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          concepto,
          monto,
          files: tempFiles.map((tempFile) => ({
            id: crypto.randomUUID(),
            file: tempFile.file,
            preview: createObjectUrl(tempFile.file),
            status: 'pending' as ComprobanteStatus,
            createdAt: tempFile.createdAt,
          })),
          status: 'pending' as ComprobanteStatus,
          createdAt: Date.now(),
        },
      ]);

      clearTempFiles();
      setShowForm(false);
    },
    [tempFiles, clearTempFiles],
  );

  const updateComprobante = useCallback(
    (id: string, concepto: string, monto: string) => {
      setGroups((prev) => {
        return prev.map((g) => {
          if (g.id !== id) return g;

          if (tempFiles.length) {
            g.files.forEach((file) => revokeObjectUrl(file.preview));

            const files = tempFiles.map((tempFile) => ({
              id: crypto.randomUUID(),
              file: tempFile.file,
              preview: createObjectUrl(tempFile.file),
              status: 'pending' as ComprobanteStatus,
              createdAt: tempFile.createdAt,
            }));

            return {
              ...g,
              concepto,
              monto,
              files,
              status: 'pending' as ComprobanteStatus,
              errorMessage: undefined,
            };
          }

          return { ...g, concepto, monto };
        });
      });

      clearTempFiles();
      setEditingId(null);
      setShowForm(false);
    },
    [tempFiles, clearTempFiles],
  );

  const removeComprobante = useCallback((id: string) => {
    setGroups((prev) => {
      const item = prev.find((g) => g.id === id);
      item?.files.forEach((file) => revokeObjectUrl(file.preview));
      return prev.filter((g) => g.id !== id);
    });
  }, []);

  const removeComprobanteFile = useCallback((comprobanteId: string, fileId: string) => {
    setGroups((prev) => {
      return prev.reduce<ComprobanteItem[]>((acc, group) => {
        if (group.id !== comprobanteId) {
          acc.push(group);
          return acc;
        }

        const fileToRemove = group.files.find((file) => file.id === fileId);
        revokeObjectUrl(fileToRemove?.preview);

        const files = group.files.filter((file) => file.id !== fileId);
        if (!files.length) return acc;

        acc.push({
          ...group,
          files,
          status: getComprobanteStatus(files),
        });
        return acc;
      }, []);
    });
  }, []);

  const updateComprobanteStatus = useCallback(
    (id: string, status: ComprobanteStatus, errorMessage?: string) => {
      setGroups((prev) =>
        prev.map((g) => (g.id === id ? { ...g, status, errorMessage } : g)),
      );
    },
    [],
  );

  const updateComprobanteFileStatus = useCallback(
    (
      comprobanteId: string,
      fileId: string,
      status: ComprobanteStatus,
      errorMessage?: string,
    ) => {
      setGroups((prev) =>
        prev.map((group) => {
          if (group.id !== comprobanteId) return group;

          const files = group.files.map((file) =>
            file.id === fileId ? { ...file, status, errorMessage } : file,
          );
          const nextStatus = getComprobanteStatus(files);

          return {
            ...group,
            files,
            status: nextStatus,
            errorMessage:
              nextStatus === 'error' ? 'Algunos archivos no pudieron enviarse' : undefined,
          };
        }),
      );
    },
    [],
  );

  // === Manejo de la galería ===
  const openGallery = useCallback(
    (
      fileUrl: string,
      mimeType?: string,
      ids?: { comprobanteId?: string; comprobanteFileId?: string; tempFileId?: string },
    ) => {
      setGalleryState({
        isOpen: true,
        fileUrl,
        mimeType: mimeType || null,
        comprobanteId: ids?.comprobanteId || null,
        comprobanteFileId: ids?.comprobanteFileId || null,
        tempFileId: ids?.tempFileId || null,
      });
    },
    [],
  );

  const closeGallery = useCallback(() => {
    setGalleryState({
      isOpen: false,
      fileUrl: null,
      mimeType: null,
      comprobanteId: null,
      comprobanteFileId: null,
      tempFileId: null,
    });
  }, []);

  const viewTempFile = useCallback(
    (id?: string) => {
      const tempFile = id ? tempFiles.find((item) => item.id === id) : tempFiles[0];
      if (tempFile) {
        openGallery(tempFile.preview, tempFile.file.type, { tempFileId: tempFile.id });
      }
    },
    [tempFiles, openGallery],
  );

  const viewComprobante = useCallback(
    (id: string, fileId?: string) => {
      const comprobante = groups.find((c) => c.id === id);
      const file = fileId
        ? comprobante?.files.find((item) => item.id === fileId)
        : comprobante?.files[0];

      if (file) {
        openGallery(file.preview, file.file.type, {
          comprobanteId: id,
          comprobanteFileId: file.id,
        });
      }
    },
    [groups, openGallery],
  );

  const canDeleteFromGallery = useMemo(() => {
    if (galleryState.tempFileId) return true;
    if (!galleryState.comprobanteId) return false;
    const item = groups.find((c) => c.id === galleryState.comprobanteId);
    return item?.status === 'pending' || item?.status === 'error';
  }, [galleryState.comprobanteId, galleryState.tempFileId, groups]);

  const deleteFromGallery = useCallback(() => {
    if (galleryState.tempFileId) {
      removeTempFile(galleryState.tempFileId);
    } else if (galleryState.comprobanteId && galleryState.comprobanteFileId) {
      removeComprobanteFile(
        galleryState.comprobanteId,
        galleryState.comprobanteFileId,
      );
    } else if (galleryState.comprobanteId) {
      removeComprobante(galleryState.comprobanteId);
    }
    closeGallery();
  }, [
    galleryState.tempFileId,
    galleryState.comprobanteId,
    galleryState.comprobanteFileId,
    removeTempFile,
    removeComprobanteFile,
    removeComprobante,
    closeGallery,
  ]);

  // === Envío de comprobantes ===
  const submitComprobante = useCallback(
    async (item: ComprobanteItem, id: string): Promise<boolean> => {
      updateComprobanteStatus(item.id, 'submitting');

      let hasError = false;
      let montoEnviado = item.files.some((file) => file.status === 'success');

      for (const comprobanteFile of item.files) {
        if (comprobanteFile.status === 'success') continue;

        updateComprobanteFileStatus(item.id, comprobanteFile.id, 'submitting');

        try {
          const montoToSend = montoEnviado ? MONTO_ARCHIVO_ADICIONAL : item.monto;

          console.log('[Comprobante] submitting file', {
            isVacuna,
            submitId: id,
            comprobanteId: item.id,
            comprobanteFileId: comprobanteFile.id,
            concepto: item.concepto,
            monto: montoToSend,
            file: {
              name: comprobanteFile.file.name,
              type: comprobanteFile.file.type,
              size: comprobanteFile.file.size,
              lastModified: comprobanteFile.file.lastModified,
            },
          });

          const res = isVacuna
            ? await postDocumentacionOtros(comprobanteFile.file, id)
            : await postComprobante({
                comprobante: comprobanteFile.file,
                concepto: item.concepto,
                monto: montoToSend,
                vetFormId: id,
              });

          if (!res) throw new Error('Error al enviar comprobante');

          montoEnviado = true;
          console.log('[Comprobante] submit success', {
            isVacuna,
            submitId: id,
            comprobanteId: item.id,
            comprobanteFileId: comprobanteFile.id,
            response: res,
          });

          updateComprobanteFileStatus(item.id, comprobanteFile.id, 'success');
        } catch (error) {
          console.error('[Comprobante] submit error', {
            isVacuna,
            submitId: id,
            comprobanteId: item.id,
            comprobanteFileId: comprobanteFile.id,
            error,
          });

          hasError = true;
          const errorMessage =
            error instanceof Error ? error.message : 'Error al enviar comprobante';
          updateComprobanteFileStatus(item.id, comprobanteFile.id, 'error', errorMessage);
        }
      }

      if (hasError) {
        updateComprobanteStatus(
          item.id,
          'error',
          'Algunos archivos no pudieron enviarse',
        );
        return false;
      }

      updateComprobanteStatus(item.id, 'success');
      return true;
    },
    [
      isVacuna,
      postComprobante,
      updateComprobanteFileStatus,
      updateComprobanteStatus,
    ],
  );

  const submitAllComprobantes = useCallback(
    async (id: string) => {
      const pendingItems = groups.filter(
        (g) => g.status === 'pending' || g.status === 'error',
      );

      let successCount = 0;
      let errorCount = 0;

      for (const item of pendingItems) {
        const success = await submitComprobante(item, id);
        if (success) successCount++;
        else errorCount++;
      }

      return {
        allSuccess: errorCount === 0 && successCount > 0,
        successCount,
        errorCount,
      };
    },
    [groups, submitComprobante],
  );

  // === Manejo del formulario ===
  const openForm = useCallback(() => {
    setEditingId(null);
    clearTempFiles();
    setShowForm(true);
  }, [clearTempFiles]);

  const closeForm = useCallback(() => {
    setEditingId(null);
    clearTempFiles();
    setShowForm(false);
  }, [clearTempFiles]);

  const startEdit = useCallback(
    (id: string) => {
      const item = groups.find((g) => g.id === id);
      if (!item || item.status !== 'pending') return null;

      clearTempFiles();
      setEditingId(id);
      setTempFiles(
        item.files.map((file) => ({
          id: crypto.randomUUID(),
          file: file.file,
          preview: createObjectUrl(file.file),
          createdAt: file.createdAt,
        })),
      );
      setShowForm(true);

      return { concepto: item.concepto, monto: item.monto };
    },
    [groups, clearTempFiles],
  );

  // === Valores derivados ===
  const hasComprobantes = groups.length > 0;
  const hasTempFiles = tempFiles.length > 0;
  const pendingComprobantes = useMemo(
    () => groups.filter((g) => g.status === 'pending' || g.status === 'error'),
    [groups],
  );

  // Ordenar comprobantes por fecha de creación (más recientes primero)
  const sortedGroups = useMemo(
    () => [...groups].sort((a, b) => b.createdAt - a.createdAt),
    [groups],
  );

  return {
    // Estado
    groups: sortedGroups,
    showForm,
    editingId,
    tempFile: tempFiles[0]?.file ?? null,
    tempPreview: tempFiles[0]?.preview ?? null,
    tempFiles,
    galleryState,
    hasComprobantes,
    hasTempFiles,
    pendingComprobantes,

    // Acciones de archivos temporales
    handleFileSelected,
    clearTempFile,
    clearTempFiles,
    removeTempFile,

    // Acciones de comprobantes
    addComprobante,
    updateComprobante,
    removeComprobante,

    // Acciones de formulario
    openForm,
    closeForm,
    startEdit,

    // Acciones de galería
    viewTempFile,
    viewComprobante,
    closeGallery,
    canDeleteFromGallery,
    deleteFromGallery,

    // Acciones de envío
    submitAllComprobantes,
  };
};

export type ComprobanteManager = ReturnType<typeof useComprobanteManager>;
